import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";

// 1. ENRICHED DATA DICTIONARY FOR PREMIUM LOOK
const servicesData = {
  "car-rental-booking": {
    title: "Car Rental Booking",
    subtitle: "Submit a request to find the perfect rental car for your trip.",
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
    overview:
      "Our car rental booking assistance helps you navigate the complex world of rental providers. Whether you need a compact car for a quick business trip or a spacious minivan for a family vacation, we simplify the process. We help you find available vehicles across major U.S. airports and city locations.",
    features: [
      {
        title: "Wide Selection",
        desc: "Access to various vehicle categories from compacts to SUVs.",
      },
      {
        title: "Transparent Guidance",
        desc: "Clear breakdown of estimated costs and provider terms.",
      },
      {
        title: "Flexible Locations",
        desc: "Support for major airport and city center pickups.",
      },
      {
        title: "Expert Assistance",
        desc: "Our team helps you navigate complex rental policies.",
      },
    ],
    steps: [
      {
        title: "Tell us your needs",
        desc: "Submit your travel dates, preferred vehicle type, and pickup location.",
      },
      {
        title: "Review options",
        desc: "We review your request and guide you through available provider options.",
      },
      {
        title: "Secure reservation",
        desc: "Finalize your booking details and prepare for your upcoming trip.",
      },
    ],
  },
  "car-rental-cancellation": {
    title: "Car Rental Cancellation",
    subtitle:
      "Understand how to cancel your request and check refund eligibility.",
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    overview:
      "Plans change, and we understand that. We provide guidance on navigating the cancellation policies of your specific rental provider. It is important to know that cancellation terms, potential fees, and refund eligibility vary heavily depending on the provider and the timing of your cancellation.",
    features: [
      {
        title: "Policy Review",
        desc: "We help you understand your specific provider's cancellation rules.",
      },
      {
        title: "Refund Guidance",
        desc: "Information on refund timelines (typically 5-7 business days).",
      },
      {
        title: "No-Show Prevention",
        desc: "Assistance in cancelling before deadlines to avoid no-show penalties.",
      },
      {
        title: "Step-by-Step Help",
        desc: "Clear instructions on how to process your cancellation request.",
      },
    ],
    steps: [
      {
        title: "Locate your confirmation",
        desc: "Have your booking reference number and email address ready.",
      },
      {
        title: "Review deadlines",
        desc: "Check if you are within the free cancellation window.",
      },
      {
        title: "Submit request",
        desc: "Contact us or the provider directly to formally cancel the reservation.",
      },
    ],
  },
  "car-rental-changes": {
    title: "Car Rental Changes",
    subtitle: "Need to update your dates or location? We can help.",
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    ),
    overview:
      "If your flight gets delayed or your itinerary shifts, adjusting your rental car details is crucial. We can assist you in changing your pickup dates, times, or drop-off locations. Please note that modifications may trigger a recalculation of your rental rate based on current pricing.",
    features: [
      {
        title: "Date Adjustments",
        desc: "Shift your pickup or drop-off dates easily.",
      },
      {
        title: "Time Modifications",
        desc: "Update your arrival time to ensure your car is held.",
      },
      {
        title: "Location Changes",
        desc: "Switch from an airport to a city pickup (subject to availability).",
      },
      {
        title: "Rate Transparency",
        desc: "We will inform you if the change affects your final price.",
      },
    ],
    steps: [
      {
        title: "Contact our team",
        desc: "Reach out with your booking reference and the new desired details.",
      },
      {
        title: "Check availability",
        desc: "We verify if the provider can accommodate the changes.",
      },
      {
        title: "Confirm updates",
        desc: "Receive an updated itinerary with your new schedule and rates.",
      },
    ],
  },
  "car-rental-modification": {
    title: "Car Rental Modification",
    subtitle: "Upgrade your vehicle or add extras to your booking.",
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
        />
      </svg>
    ),
    overview:
      "Realized you need more space for luggage? Traveling with a toddler and need a child seat? We assist customers in modifying their existing reservations to add extras, upgrade vehicle classes, or include additional drivers to make the trip more comfortable.",
    features: [
      {
        title: "Vehicle Upgrades",
        desc: "Switch from a sedan to an SUV or minivan.",
      },
      {
        title: "Additional Drivers",
        desc: "Add a spouse or colleague to the rental agreement.",
      },
      {
        title: "Special Equipment",
        desc: "Request child seats, GPS navigation, or toll passes.",
      },
      {
        title: "Insurance Options",
        desc: "Review and add supplementary coverage options.",
      },
    ],
    steps: [
      {
        title: "Identify your needs",
        desc: "Decide what extras or upgrades will improve your trip.",
      },
      {
        title: "Request modification",
        desc: "Provide us your booking details and requested additions.",
      },
      {
        title: "Finalize changes",
        desc: "We coordinate with the provider to secure the requested items.",
      },
    ],
  },
  "24-7-support-car-rental": {
    title: "24/7 Support",
    subtitle: "We are here to help you around the clock.",
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
    ),
    overview:
      "Travel rarely sticks to a 9-to-5 schedule. That is why our support channels are designed to assist you with urgent booking enquiries, last-minute cancellations, or emergency modifications whenever you need them, day or night.",
    features: [
      {
        title: "Always Open",
        desc: "Assistance available 24 hours a day, 7 days a week.",
      },
      {
        title: "Multiple Channels",
        desc: "Reach us via phone or email for your convenience.",
      },
      {
        title: "Emergency Modifications",
        desc: "Help with sudden flight cancellations or delays.",
      },
      {
        title: "Global Access",
        desc: "Support available no matter what time zone you are in.",
      },
    ],
    steps: [
      {
        title: "Encounter an issue",
        desc: "Whether it's a flight delay or a sudden change of plans.",
      },
      {
        title: "Reach out immediately",
        desc: "Call our hotline or send an urgent email.",
      },
      {
        title: "Get swift resolution",
        desc: "Our team works to find a solution to your rental problem.",
      },
    ],
  },
  "long-term-car-rental": {
    title: "Long-Term Car Rental",
    subtitle: "Extended rental assistance for corporate or personal needs.",
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
    overview:
      "Whether you are relocating, on an extended business assignment, or simply taking a long vacation, renting a car for weeks or months requires different planning. We help coordinate long-term rentals, which often feature specialized monthly rates and flexible terms.",
    features: [
      {
        title: "Better Rates",
        desc: "Access to extended-term pricing that beats daily rates.",
      },
      {
        title: "Vehicle Swaps",
        desc: "Options to change vehicles during your extended rental period.",
      },
      {
        title: "Flexible Terms",
        desc: "Easily extend your rental month-by-month.",
      },
      {
        title: "Corporate Solutions",
        desc: "Tailored assistance for traveling business professionals.",
      },
    ],
    steps: [
      {
        title: "Define your timeline",
        desc: "Determine how many weeks or months you will need the vehicle.",
      },
      {
        title: "Compare long-term rates",
        desc: "We help you find the most cost-effective extended options.",
      },
      {
        title: "Sign & Drive",
        desc: "Complete the extended agreement and enjoy your mobility.",
      },
    ],
  },
};

const ServicePage = () => {
  const { serviceId } = useParams();

  // Redirect to a default tab if URL doesn't match our data
  if (!serviceId || !servicesData[serviceId]) {
    return <Navigate to="/services/car-rental-booking" replace />;
  }

  const activePage = servicesData[serviceId];

  const handlePhoneClick = () => {
    if (window.gtag) {
      window.gtag("config", "AW-18343552397/3FAKCIr6l9ccEI3L8apE", {
        phone_conversion_number: "1-877-991-0909",
      });
    }
  };

  return (
    <main className="w-full bg-[#f4f7fb] font-sans text-slate-800 pb-16 lg:pb-24 min-h-screen selection:bg-[#e32b22] selection:text-white">
      {/* 1. STYLISH HERO SECTION */}
      <section className="relative w-full bg-[#091b3e] text-white pt-16 lg:pt-24 pb-28 lg:pb-36 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Subtle Background Pattern */}
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        ></div>

        <div className="relative max-w-4xl mx-auto flex flex-col items-center text-center">
          <div className="bg-[#e32b22] text-white p-4 rounded-2xl mb-6 shadow-xl shadow-red-900/20 transform transition-transform hover:scale-105 duration-300">
            {activePage.icon}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 tracking-tight">
            {activePage.title}
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 leading-relaxed max-w-2xl font-light">
            {activePage.subtitle}
          </p>
        </div>
      </section>

      {/* 2. MAIN LAYOUT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 lg:-mt-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* LEFT SIDEBAR */}
          <div className="lg:col-span-4 flex flex-col gap-6 lg:sticky lg:top-8">
            {/* Services Navigation */}
            <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6 sm:p-8">
              <h3 className="font-bold text-[#091b3e] text-lg mb-4 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-[#e32b22]"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M10 13l-5-5 5-5v10z" />
                </svg>
                Our Services
              </h3>
              <div className="h-px bg-slate-100 w-full mb-4"></div>

              <ul className="space-y-2">
                {Object.keys(servicesData).map((slug) => {
                  const isActive = serviceId === slug;
                  return (
                    <li key={slug}>
                      <Link
                        to={`/services/${slug}`}
                        className={`group flex items-center justify-between p-3.5 rounded-xl text-sm transition-all duration-200 ${
                          isActive
                            ? "bg-[#091b3e] text-white font-bold shadow-md"
                            : "font-semibold text-slate-600 hover:bg-slate-50 hover:text-[#e32b22]"
                        }`}
                      >
                        {servicesData[slug].title}
                        {isActive && (
                          <svg
                            className="w-4 h-4 text-[#e32b22]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="3"
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Quick Contact Card */}
            <div className="bg-gradient-to-br from-[#091b3e] to-[#122c5e] rounded-3xl shadow-lg p-8 text-white">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mb-6">
                <svg
                  className="w-6 h-6 text-[#e32b22]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              <h3 className="font-bold text-xl mb-2">Need direct help?</h3>
              <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                Speak directly to our support team regarding your{" "}
                {activePage.title.toLowerCase()} enquiry.
              </p>
              <a
                href="tel:+18779910909"
                onClick={handlePhoneClick}
                className="block text-center bg-[#e32b22] hover:bg-red-700 text-white font-bold py-3.5 px-6 rounded-xl transition-colors text-sm w-full shadow-lg shadow-red-900/30"
              >
                Call 1-877-991-0909
              </a>
            </div>
          </div>

          {/* RIGHT CONTENT AREA */}
          <div className="lg:col-span-8 flex flex-col gap-8">
            {/* Overview Card */}
            <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6 sm:p-10">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#091b3e] mb-5">
                Overview
              </h2>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                {activePage.overview}
              </p>
            </div>

            {/* Features Grid */}
            <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6 sm:p-10">
              <h2 className="text-xl sm:text-2xl font-extrabold text-[#091b3e] mb-6">
                Service Highlights
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {activePage.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-[#e32b22]/30 transition-colors"
                  >
                    <div className="bg-[#e32b22]/10 p-2 rounded-lg flex-shrink-0 mt-0.5">
                      <svg
                        className="w-5 h-5 text-[#e32b22]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="2.5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#091b3e] text-[15px] mb-1">
                        {feature.title}
                      </h4>
                      <p className="text-slate-500 text-sm leading-relaxed">
                        {feature.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* How It Works (Steps) Card */}
            <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6 sm:p-10">
              <h2 className="text-xl sm:text-2xl font-extrabold text-[#091b3e] mb-8">
                How it works
              </h2>
              <div className="space-y-8 relative before:absolute before:inset-0 before:ml-[1.125rem] sm:before:ml-[1.4rem] before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-[#e32b22] before:via-slate-200 before:to-transparent">
                {activePage.steps.map((step, index) => (
                  <div
                    key={index}
                    className="relative flex items-start md:justify-center"
                  >
                    {/* Circle Indicator */}
                    <div className="absolute left-0 md:left-1/2 flex h-9 w-9 sm:h-11 sm:w-11 items-center justify-center rounded-full bg-[#091b3e] ring-4 ring-white md:-translate-x-1/2 shadow-md">
                      <span className="text-white font-bold text-sm sm:text-base">
                        {index + 1}
                      </span>
                    </div>

                    {/* Step Content Box */}
                    <div
                      className={`mt-1 sm:mt-2 w-full md:w-5/12 pl-14 sm:pl-16 md:pl-0 ${index % 2 === 0 ? "md:pr-16 md:text-right md:mr-auto" : "md:pl-16 md:ml-auto"}`}
                    >
                      <h4 className="text-lg font-bold text-[#091b3e] mb-2">
                        {step.title}
                      </h4>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Disclaimer / Bottom Alert */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 text-sm">
              <svg
                className="w-6 h-6 text-[#091b3e] flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-slate-500 leading-relaxed">
                By submitting a request for{" "}
                <span className="font-semibold text-slate-700">
                  {activePage.title.toLowerCase()}
                </span>
                , you acknowledge that we act as an independent service
                provider. Final approvals, rates, and policies rely strictly on
                the third-party car rental company.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicePage;
